import {
  date,
  index,
  integer,
  numeric,
  pgEnum,
  pgTable,
  serial,
  text,
  timestamp,
} from "drizzle-orm/pg-core";
import { createSchemaFactory } from "drizzle-zod";
import { z } from "zod";

import { users } from "./auth";
import { playerProfiles } from "./player-profiles";
import { timestamps } from "./shared";
import { tournamentDivisionTeams } from "./tournament-division-teams";

const { createSelectSchema } = createSchemaFactory({ zodInstance: z });

export const invoicePaymentStatusEnum = pgEnum("invoice_payment_status", [
  "authorized",
  "captured",
  "voided",
  "capture_failed",
]);

export const invoices = pgTable(
  "invoices",
  {
    id: serial().primaryKey(),
    transactionKey: text().notNull().unique(),
    purchaserId: text()
      .notNull()
      .references(() => users.id),
    paymentStatus: invoicePaymentStatusEnum().notNull().default("captured"),
    captureTransactionKey: text(),
    ...timestamps,
  },
  (table) => [index("invoices_purchaser_id_idx").on(table.purchaserId)],
);

export const selectInvoiceSchema = createSelectSchema(invoices);

export type Invoice = z.infer<typeof selectInvoiceSchema>;

export const tshirtSizeEnum = pgEnum("tshirt-size", [
  "xs",
  "sm",
  "m",
  "l",
  "xl",
  "xxl",
]);

export const tshirtSizeSchema = createSelectSchema(tshirtSizeEnum);

export type TshirtSize = z.infer<typeof tshirtSizeSchema>;

export const memberships = pgTable(
  "memberships",
  {
    id: serial().primaryKey(),
    validUntil: date().notNull(),
    profileId: integer()
      .notNull()
      .references(() => playerProfiles.id),
    invoiceId: integer().references(() => invoices.id),
    tshirtSize: tshirtSizeEnum(),
    tshirtPickedUpAt: timestamp(),
    tshirtPickedUpBy: text().references(() => users.id),
    revokedAt: timestamp(),
    revokedReason: text(),
    ...timestamps,
  },
  (table) => [
    index("memberships_profile_id_idx").on(table.profileId),
    index("memberships_profile_valid_idx").on(
      table.profileId,
      table.validUntil,
    ),
    index("memberships_invoice_id_idx").on(table.invoiceId),
    index("memberships_tshirt_picked_up_by_idx").on(table.tshirtPickedUpBy),
  ],
);

export const selectMembershipSchema = createSelectSchema(memberships);

export type Membership = z.infer<typeof selectMembershipSchema>;

export const tournamentRegistrations = pgTable(
  "tournamentRegistrations",
  {
    id: serial().primaryKey(),
    tournamentDivisionTeamId: integer()
      .notNull()
      .references(() => tournamentDivisionTeams.id),
    invoiceId: integer()
      .notNull()
      .references(() => invoices.id),
    ...timestamps,
  },
  (table) => [
    index("tournament_registrations_team_id_idx").on(
      table.tournamentDivisionTeamId,
    ),
    index("tournament_registrations_invoice_id_idx").on(table.invoiceId),
  ],
);

export const refunds = pgTable(
  "refunds",
  {
    id: serial().primaryKey(),
    invoiceId: integer()
      .notNull()
      .references(() => invoices.id),
    tournamentDivisionTeamId: integer().references(
      () => tournamentDivisionTeams.id,
    ),
    membershipId: integer().references(() => memberships.id),
    transactionKey: text().notNull().unique(),
    originalTransactionKey: text().notNull(),
    amount: numeric({ mode: "number" }).notNull(),
    fee: numeric({ mode: "number" }).notNull(),
    ...timestamps,
  },
  (table) => [
    index("refunds_invoice_id_idx").on(table.invoiceId),
    index("refunds_tournament_division_team_id_idx").on(
      table.tournamentDivisionTeamId,
    ),
    index("refunds_membership_id_idx").on(table.membershipId),
  ],
);

export const selectRefundSchema = createSelectSchema(refunds);

export type Refund = z.infer<typeof selectRefundSchema>;
